import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import SideBarArrow from '../components/img/SideBarArrow.svg';
import SideBarArrowComponent from '../components/img/SidebarArrow';
import AdminButtons from './AdminPanel/AdminButtons';
import useAuth from '../hooks/useAuth';
import SupportModalContact from './Modals/SupportModalContact';
import AcceleratePromo from './AcceleratePromo';

// Path arrays used to auto-close the submenus when navigating away
const submenuPaths = ['deal-registration', 'prospects', 'customers'];
const submenuContactPaths = ['request-a-demo', 'sales-team'];
const submenuProspectsPaths = ['/prospects/active', '/prospects/lost'];
const submenuCustomersPaths = [
  '/customers/active',
  '/customers/expired',
  '/customers/renewals',
];

const DashboardSidebar = () => {
  const [submenuContact, setSubmenuContact] = useState(false);
  const [submenuProspects, setSubmenuProspects] = useState(false);
  const [submenuCustomers, setSubmenuCustomers] = useState(false);
  const [submenuDealsPanel, setSubMenuDealsPanel] = useState(false);
  const [isWazuhPanelOpen, setIsWazuhPanelOpen] = useState(false);
  const [isSupportPortalModalOpen, setIsSupportPortalModalOpen] =
    useState(false);

  const userinfo = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const path = location.pathname.split('/')[1];
  const pathContact = location.pathname.split('/')[2];

  // Helper functions for pathname checks
  const isDealsPanelPath = () => {
    return (
      pathname === '/deal-registration' ||
      path === 'prospects' ||
      path === 'customers'
    );
  };

  const isContactUsPath = () => {
    return path === 'contact-us';
  };

  const isActiveLink = (link) => {
    return pathname === link;
  };

  // Menu handlers
  const handleSubMenuContact = () => {
    if (submenuContact === false) {
      navigate('/contact-us/sales-team');
      setSubMenuDealsPanel(false);
      setSubmenuProspects(false);
      setSubmenuCustomers(false);
      setIsWazuhPanelOpen(false);
    }
    setSubmenuContact(!submenuContact);
  };

  const handleSubMenuProspects = () => {
    if (submenuProspects === false) {
      navigate('/prospects/active');
    }
    setSubmenuProspects(!submenuProspects);
  };

  const handleSubMenuDealsPanel = () => {
    if (submenuDealsPanel === false) {
      navigate('/deal-registration');
      setSubmenuContact(false);
      setIsWazuhPanelOpen(false);
    }
    setSubMenuDealsPanel(!submenuDealsPanel);
  };

  const handleSubMenuCustomers = () => {
    if (submenuCustomers === false) {
      navigate('/customers/active');
    }
    setSubmenuCustomers(!submenuCustomers);
  };

  const closeAllSubmenus = () => {
    setSubMenuDealsPanel(false);
    setSubmenuContact(false);
    setSubmenuProspects(false);
    setSubmenuCustomers(false);
  };

  // Auto-close menus when navigating away
  useEffect(() => {
    if (!submenuProspectsPaths.includes(pathname)) {
      setSubmenuProspects(false);
    }
  }, [pathname]);

  useEffect(() => {
    if (!submenuCustomersPaths.includes(pathname)) {
      setSubmenuCustomers(false);
    }
  }, [pathname]);

  useEffect(() => {
    if (!submenuPaths.includes(path)) {
      setSubMenuDealsPanel(false);
    }
  }, [path]);

  useEffect(() => {
    if (!submenuContactPaths.includes(pathContact)) {
      setSubmenuContact(false);
    }
  }, [pathContact]);

  // Menu configuration
  const list = [
    {
      name: 'Home',
      link: '/dashboard',
    },
    {
      name: 'Deals Panel',
      function: handleSubMenuDealsPanel,
      submenuvalue: submenuDealsPanel,
      submenu: [
        {
          name: 'Deal registration',
          link: '/deal-registration',
        },
        {
          name: 'Prospects',
          function: handleSubMenuProspects,
          submenuvalue: submenuProspects,
          submenu: [
            { name: 'Active', link: '/prospects/active' },
            { name: 'Lost', link: '/prospects/lost' },
          ],
        },
        {
          name: 'Customers',
          function: handleSubMenuCustomers,
          submenuvalue: submenuCustomers,
          submenu: [
            { name: 'Active', link: '/customers/active' },
            { name: 'Expired', link: '/customers/expired' },
            { name: 'Renewals', link: '/customers/renewals' },
          ],
        },
      ],
    },
    { name: 'Analytics', link: '/analytics' },
    { name: 'Resources', link: '/resources' },
    { name: 'Wazuh Portal' },
    {
      name: 'Contact Us',
      function: handleSubMenuContact,
      submenu: [
        { name: 'Wazuh Sales Team', link: '/contact-us/sales-team' },
        { name: 'Request a Demo', link: '/contact-us/request-a-demo' },
        { name: 'Support portal' },
      ],
    },
  ];

  // Render menu arrow component. The arrow inherits its color from `currentColor`,
  // so it turns white both on the blue active background and in dark mode.
  const renderMenuArrow = (
    isOpen,
    className = 'ml-2 w-[11px] h-[18px] mt-1'
  ) => (
    <SideBarArrowComponent
      className={`${className} transition ease-out duration-300 ${
        isOpen ? 'rotate-180 text-white' : 'text-mainblack dark:text-white'
      }`}
    />
  );

  // Render active menu item content
  const renderActiveContent = (itemName) => (
    <div className='font-bold flex text-white'>
      <img
        alt='blue-arrow'
        className='relative right-[14px] w-1'
        src={SideBarArrow}
      />
      <span className='relative right-1'>{itemName}</span>
    </div>
  );

  // Check if menu item should be active
  const isMenuActive = (item) => {
    if (item.link) {
      return isActiveLink(item.link);
    }
    if (item.name === 'Deals Panel') {
      return isDealsPanelPath() || submenuDealsPanel;
    }
    if (item.name === 'Contact Us') {
      return isContactUsPath() || submenuContact;
    }
    return false;
  };

  // Check if menu should have blue background
  const shouldHaveBlueBackground = (item) => {
    if (item.link) {
      return isActiveLink(item.link);
    }
    if (item.name === 'Deals Panel') {
      return submenuDealsPanel || isDealsPanelPath();
    }
    if (item.name === 'Contact Us') {
      return submenuContact || isContactUsPath();
    }
    return false;
  };

  return (
    <div className='relative top-[65px]'>
      <div className='fixed hide-scrollbar 3xl:hidden hidden lg:block h-[calc(100vh-65px)] left-0 w-72 bg-[#F6F6F6] dark:bg-greybg z-0 overflow-y-auto'>
        <div className='my-5 mb-24'>
          <div className='flex flex-col h-full ml-4'>
            <div>
              {list.map((item) => (
                <React.Fragment key={item.name}>
                  {item.name === 'Wazuh Portal' ? (
                    <div>
                      {userinfo.role === 'wazuh' && (
                        <AdminButtons
                          setWazuhPanel={setIsWazuhPanelOpen}
                          open={isWazuhPanelOpen}
                          closeAllSubmenus={closeAllSubmenus}
                          navigate={navigate}
                        />
                      )}
                    </div>
                  ) : (
                    <div className='flex'>
                      <div className='w-full flex flex-col font-manrope text-base text-gray-800 hover:text-redpigment ease-in duration-200 cursor-pointer'>
                        {item.link ? (
                          <Link
                            className={`${
                              shouldHaveBlueBackground(item)
                                ? 'bg-mainblue w-[85%] rounded-r-xl'
                                : ''
                            } flex dark:text-white pl-8 font-normal hover:text-blue-500 py-2 border-l-[2px] border-[#EAEAEA]`}
                            to={item.link}
                            onClick={item.function}
                          >
                            {isMenuActive(item) ? (
                              renderActiveContent(item.name)
                            ) : (
                              <div
                                className={`${
                                  (item.name === 'Contact Us' &&
                                    submenuContact) ||
                                  (item.name === 'Deals Panel' &&
                                    submenuDealsPanel) ||
                                  (item.name === 'Customers' &&
                                    submenuCustomers)
                                    ? 'font-semibold'
                                    : ''
                                }`}
                              >
                                <span>{item.name}</span>
                              </div>
                            )}
                            {item.name === 'Deals Panel' &&
                              item.submenu &&
                              renderMenuArrow(submenuDealsPanel)}
                            {item.name === 'Contact Us' &&
                              item.submenu &&
                              renderMenuArrow(submenuContact)}
                            {item.name === 'Prospects' && item.submenu && (
                              <SideBarArrowComponent
                                className={`ml-2 w-[11px] h-[18px] mt-1 text-mainblack dark:text-white transition ease-out duration-300 ${
                                  submenuProspects && 'rotate-180'
                                }`}
                              />
                            )}
                            {item.name === 'Customers' && item.submenu && (
                              <SideBarArrowComponent
                                className={`ml-2 w-[11px] h-[18px] mt-1 text-mainblack dark:text-white transition ease-out duration-300 ${
                                  submenuCustomers && 'rotate-180'
                                }`}
                              />
                            )}
                          </Link>
                        ) : (
                          <div
                            className={`${
                              shouldHaveBlueBackground(item)
                                ? 'bg-mainblue w-[85%] rounded-r-xl'
                                : ''
                            } flex dark:text-white pl-8 font-normal hover:text-blue-500 py-2 border-l-[2px] border-[#EAEAEA] cursor-pointer justify-between w-[85%]`}
                            onClick={(e) => {
                              e.preventDefault();
                              if (item.function) item.function();
                            }}
                          >
                            {isMenuActive(item) ? (
                              renderActiveContent(item.name)
                            ) : (
                              <div
                                className={`${
                                  (item.name === 'Contact Us' &&
                                    submenuContact) ||
                                  (item.name === 'Deals Panel' &&
                                    submenuDealsPanel) ||
                                  (item.name === 'Customers' &&
                                    submenuCustomers)
                                    ? 'font-semibold'
                                    : ''
                                }`}
                              >
                                <span>{item.name}</span>
                              </div>
                            )}
                            {item.name === 'Deals Panel' &&
                              item.submenu &&
                              renderMenuArrow(
                                submenuDealsPanel,
                                'mr-7 w-[11px] h-[18px] mt-1'
                              )}
                            {item.name === 'Contact Us' &&
                              item.submenu &&
                              renderMenuArrow(
                                submenuContact,
                                'mr-7 w-[11px] h-[18px] mt-1'
                              )}
                            {item.name === 'Prospects' && item.submenu && (
                              <SideBarArrowComponent
                                className={`ml-2 w-[11px] h-[18px] mt-1 text-mainblack dark:text-white transition ease-out duration-300 ${
                                  submenuProspects && 'rotate-180'
                                }`}
                              />
                            )}
                            {item.name === 'Customers' && item.submenu && (
                              <SideBarArrowComponent
                                className={`ml-2 w-[11px] h-[18px] mt-1 text-mainblack dark:text-white transition ease-out duration-300 ${
                                  submenuCustomers && 'rotate-180'
                                }`}
                              />
                            )}
                          </div>
                        )}

                        {/* Deals Panel Submenu */}
                        {submenuDealsPanel &&
                          item.name === 'Deals Panel' &&
                          item.submenu && (
                            <div className='pl-[19px]'>
                              <div className='mt-4'>
                                {item.submenu.map((subItem) => (
                                  <div key={subItem.name}>
                                    {!subItem.submenu && (
                                      <Link
                                        className='flex pb-4 pl-[35.5px] dark:text-white hover:text-blue-500 border-l-[2.8px] border-[#EAEAEA]'
                                        to={subItem.link}
                                      >
                                        {isActiveLink(subItem.link) ? (
                                          <div className='relative right-1 flex text-blue-500'>
                                            <img
                                              alt='blue-arrow'
                                              className='relative right-3 w-1'
                                              src={SideBarArrow}
                                            />
                                            <span>{subItem.name}</span>
                                          </div>
                                        ) : (
                                          <span>{subItem.name}</span>
                                        )}
                                      </Link>
                                    )}
                                    {subItem.submenu && (
                                      <>
                                        <div
                                          onClick={subItem.function}
                                          className='flex py-4 dark:text-white hover:text-blue-500 border-l-[2.8px] border-[#EAEAEA] justify-between w-[85%]'
                                        >
                                          <span
                                            className={`${
                                              subItem.submenuvalue
                                                ? 'relative right-1 text-blue-500'
                                                : ''
                                            } pl-[35.5px] flex`}
                                          >
                                            {subItem.submenuvalue && (
                                              <img
                                                alt='blue-arrow'
                                                className='relative right-3 w-1'
                                                src={SideBarArrow}
                                              />
                                            )}
                                            {subItem.name}
                                          </span>
                                          <SideBarArrowComponent
                                            className={`mr-7 mt-1 w-[11px] h-[18px] text-mainblack dark:text-white transition ease-out duration-300 ${
                                              subItem.submenuvalue &&
                                              'rotate-180'
                                            }`}
                                          />
                                        </div>
                                        {subItem.submenuvalue && (
                                          <div className='ml-5 pl-[28px] dark:text-white border-l-[2.8px] border-[#EAEAEA]'>
                                            {subItem.submenu.map(
                                              (nestedItem) => (
                                                <Link
                                                  key={nestedItem.name}
                                                  className={`${
                                                    isActiveLink(
                                                      nestedItem.link
                                                    )
                                                      ? 'text-blue-500'
                                                      : ''
                                                  } flex my-4 hover:text-blue-500`}
                                                  to={nestedItem.link}
                                                >
                                                  {nestedItem.name}
                                                </Link>
                                              )
                                            )}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        {/* Contact Us Submenu */}
                        {submenuContact &&
                          item.name === 'Contact Us' &&
                          item.submenu && (
                            <div className='pl-[19px]'>
                              <div className='border-l-[2.8px] border-[#EAEAEA] mt-4 pl-[35.5px]'>
                                {item.submenu.map((subItem) => (
                                  <div
                                    key={subItem.name}
                                    className='my-4 font-normal dark:text-white hover:text-blue-500'
                                  >
                                    {subItem.name === 'Support portal' ? (
                                      <div className='flex'>
                                        <SupportModalContact
                                          onModalOpen={() =>
                                            setIsSupportPortalModalOpen(true)
                                          }
                                          onModalClose={() =>
                                            setIsSupportPortalModalOpen(false)
                                          }
                                          isActive={isSupportPortalModalOpen}
                                        />
                                      </div>
                                    ) : (
                                      <Link className='flex' to={subItem.link}>
                                        {pathContact === 'sales-team' &&
                                        subItem.name === 'Wazuh Sales Team' &&
                                        !isSupportPortalModalOpen ? (
                                          <div className='flex text-blue-500'>
                                            <img
                                              alt='blue-arrow'
                                              className='relative right-4 w-1'
                                              src={SideBarArrow}
                                            />
                                            <span className='relative right-1'>
                                              {subItem.name}
                                            </span>
                                          </div>
                                        ) : pathContact === 'request-a-demo' &&
                                          subItem.name === 'Request a Demo' &&
                                          !isSupportPortalModalOpen ? (
                                          <div className='flex text-blue-500'>
                                            <img
                                              alt='blue-arrow'
                                              className='relative right-4 w-1'
                                              src={SideBarArrow}
                                            />
                                            <span className='relative right-1'>
                                              {subItem.name}
                                            </span>
                                          </div>
                                        ) : (
                                          <span>{subItem.name}</span>
                                        )}
                                      </Link>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        {/* Prospects Submenu */}
                        {submenuProspects &&
                          item.name === 'Prospects' &&
                          item.submenu && (
                            <div>
                              <div className='border-l-2 border-blue-500 mt-4 pl-6'>
                                {item.submenu.map((subItem) => (
                                  <div
                                    key={subItem.name}
                                    className='my-4 font-normal hover:text-blue-500'
                                  >
                                    <Link className='flex' to={subItem.link}>
                                      {pathContact === 'active' &&
                                      subItem.name === 'Active' ? (
                                        <div className='font-bold flex text-blue-500'>
                                          <img
                                            alt='blue-arrow'
                                            className='relative right-2 w-1'
                                            src={SideBarArrow}
                                          />
                                          <span>{subItem.name}</span>
                                        </div>
                                      ) : pathContact === 'lost' &&
                                        subItem.name === 'Lost' ? (
                                        <div className='font-bold flex text-blue-500'>
                                          <img
                                            alt='blue-arrow'
                                            className='relative right-2 w-1'
                                            src={SideBarArrow}
                                          />
                                          <span>{subItem.name}</span>
                                        </div>
                                      ) : (
                                        <span>{subItem.name}</span>
                                      )}
                                    </Link>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        {/* Customers Submenu */}
                        {submenuCustomers &&
                          item.name === 'Customers' &&
                          item.submenu && (
                            <div>
                              <div className='border-l-2 border-blue-500 mt-4 pl-6'>
                                {item.submenu.map((subItem) => (
                                  <div
                                    key={subItem.name}
                                    className='my-4 font-normal hover:text-blue-500'
                                  >
                                    <Link className='flex' to={subItem.link}>
                                      {isActiveLink(subItem.link) ? (
                                        <div className='font-bold flex text-blue-500'>
                                          <img
                                            alt='blue-arrow'
                                            className='relative right-2 w-1'
                                            src={SideBarArrow}
                                          />
                                          <span>{subItem.name}</span>
                                        </div>
                                      ) : (
                                        <span>{subItem.name}</span>
                                      )}
                                    </Link>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className='mt-10 mr-10'>
              <AcceleratePromo />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSidebar;
